import { useCallback, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "src/store/store";
import {
  getUseCaseConfigs,
  createUseCaseConfig,
  updateUseCaseConfig,
  resetSaveStatus,
  resetGetConfigsStatus,
} from "src/store/auditConfigSlice";
import { UseCaseConfig } from "../shared/types";

export interface UseUseCaseConfigReturn {
  configs: UseCaseConfig[];
  isLoading: boolean;
  isError: boolean;
  fetchError: boolean;
  authError: boolean;
  isSaving: boolean;
  saveSuccess: boolean;
  saveError: boolean;
  configById: (id: string) => UseCaseConfig | undefined;
  saveUseCase: (config: UseCaseConfig) => void;
  updateUseCase: (config: UseCaseConfig) => void;
  resetSave: () => void;
  retryFetch: () => void;
}

export function useUseCaseConfig(): UseUseCaseConfigReturn {
  const dispatch = useAppDispatch();

  const { useCaseConfigs, getConfigsLoading, saveConfigLoading } = useAppSelector(
    (state) => state.auditConfigSlice,
  );

  const { alias, userLoading } = useAppSelector((state) => state.user);

  // Fetch configs once alias is known.  Retry after failure on remount.
  useEffect(() => {
    if (!alias) return;
    if (getConfigsLoading === "idle") dispatch(getUseCaseConfigs());
  }, [dispatch, alias, getConfigsLoading]);

  // Reset rejected state on mount so a remount retries automatically.
  useEffect(() => {
    if (getConfigsLoading === "rejected") dispatch(resetGetConfigsStatus());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const aliasResolved = userLoading === "fulfilled" || userLoading === "rejected";
  const isLoading = !aliasResolved
    ? true
    : alias
      ? getConfigsLoading === "idle" || getConfigsLoading === "pending"
      : false;

  const authError = aliasResolved && !alias;
  const fetchError = getConfigsLoading === "rejected";
  const isError = authError || fetchError;

  const isSaving = saveConfigLoading === "pending";
  const saveSuccess = saveConfigLoading === "fulfilled";
  const saveError = saveConfigLoading === "rejected";

  const configById = useCallback(
    (id: string) => useCaseConfigs.find((c) => c.id === id),
    [useCaseConfigs],
  );

  const saveUseCase = useCallback(
    (config: UseCaseConfig) => {
      dispatch(createUseCaseConfig(config));
    },
    [dispatch],
  );

  const updateUseCase = useCallback(
    (config: UseCaseConfig) => {
      dispatch(updateUseCaseConfig(config));
    },
    [dispatch],
  );

  const resetSave = useCallback(() => {
    dispatch(resetSaveStatus());
  }, [dispatch]);

  const retryFetch = useCallback(() => {
    dispatch(resetGetConfigsStatus());
  }, [dispatch]);

  return {
    configs: useCaseConfigs,
    isLoading,
    isError,
    fetchError,
    authError,
    isSaving,
    saveSuccess,
    saveError,
    configById,
    saveUseCase,
    updateUseCase,
    resetSave,
    retryFetch,
  };
}
